/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import {
  X,
  ExternalLink,
  Target,
  Lightbulb,
  TrendingUp,
  CheckCircle2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface ProjectModalProps {
  project: any;
  isOpen: boolean;
  onClose: () => void;
}

export default function ProjectModal({
  project,
  isOpen,
  onClose,
}: ProjectModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 lg:p-8">
      <div
        className={cn(
          'absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity duration-500',
          isOpen ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      />

      <div
        className={cn(
          'relative w-full max-w-4xl max-h-[90vh] overflow-y-auto glass-dark border border-white/10 rounded-[2.5rem] shadow-2xl custom-scrollbar z-10',
          isOpen ? 'animate-reveal' : 'animate-dismiss',
        )}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-5 sm:right-5 lg:top-6 lg:right-6 p-2 sm:p-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md transition-all z-20 cursor-pointer shadow-lg active:scale-95"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" />
        </button>

        <div className="p-6 sm:p-8 lg:p-12 space-y-6 sm:space-y-8 lg:space-y-10">
          {/* Header */}
          <div className="space-y-3 sm:space-y-4 pr-10 sm:pr-12">
            <div
              className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-gradient leading-[1.2] pb-1 sm:pb-2 break-words"
              dangerouslySetInnerHTML={{ __html: project.title || '' }}
              suppressHydrationWarning
            />
            <div className="flex flex-wrap gap-2">
              {(project.tech || []).map((t: string, idx: number) => (
                <span
                  key={idx}
                  className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            <div className="space-y-6 sm:space-y-8">
              {/* Main Description / Problem */}
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-primary font-bold uppercase tracking-widest text-[10px] sm:text-xs">
                  <Target className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Overview
                </div>
                <p
                  className="text-foreground text-base sm:text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html:
                      project.problemStatement || project.description || '',
                  }}
                  suppressHydrationWarning
                />
              </div>

              {/* Solution */}
              {project.solution && (
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 text-blue-400 font-bold uppercase tracking-widest text-[10px] sm:text-xs">
                    <Lightbulb className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Details
                  </div>
                  <p
                    className="text-muted-foreground text-sm sm:text-base leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: project.solution || '' }}
                    suppressHydrationWarning
                  />
                </div>
              )}

              {/* Impact */}
              {project.impact && (
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 text-green-400 font-bold uppercase tracking-widest text-[10px] sm:text-xs">
                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Achievements / Impact
                  </div>
                  <p
                    className="text-muted-foreground text-sm sm:text-base leading-relaxed italic"
                    dangerouslySetInnerHTML={{ __html: project.impact || '' }}
                    suppressHydrationWarning
                  />
                </div>
              )}
            </div>

            <div className="space-y-6 sm:space-y-8">
              {/* Key Features / Highlights */}
              {project.keyFeatures && project.keyFeatures.length > 0 && (
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="text-lg sm:text-xl font-bold">
                    Key Highlights
                  </h3>
                  <div className="grid grid-cols-1 gap-2 sm:gap-3">
                    {project.keyFeatures.map((feature: string, i: number) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-2xl bg-white/5 border border-white/5"
                      >
                        <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0 mt-0.5" />
                        <span
                          className="text-sm font-medium text-muted-foreground"
                          dangerouslySetInnerHTML={{ __html: feature || '' }}
                          suppressHydrationWarning
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {project.link && project.link !== '#' && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 sm:gap-3 w-full py-3 sm:py-4 rounded-2xl bg-foreground text-background text-sm sm:text-base font-bold hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    View Resource
                    <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
